import React, { type ElementType, type ReactNode } from 'react';
import './Card.css';

export interface CardProps {
  children?: ReactNode;
  /** The element to render: div (default), article, li, a, or a router Link. */
  as?: ElementType;
  /** Hover border and press scale, for cards that link or open something. */
  interactive?: boolean;
  /** An image on top: "full" (16:9, edge to edge) or "inset" (8px margin, for logos). */
  media?: ReactNode;
  mediaFit?: 'full' | 'inset';
  /** One status pill over the image, top-left. */
  status?: ReactNode;
  /** none: the caller pads (lists inside a card). */
  padding?: 'default' | 'none';
  className?: string;
  [key: string]: unknown;
}

/** The base for content cards (DESIGN_SYSTEM.md "Card", spec 4.7). Never mix two card styles in one list. */
export function Card({
  children, as: Element = 'div', interactive, media, mediaFit = 'full', status, padding = 'default', className, ...rest
}: CardProps) {
  const classes = [
    'ui-card',
    interactive && 'ui-card--interactive',
    padding === 'none' && 'ui-card--flush',
    className,
  ].filter(Boolean).join(' ');
  return (
    <Element className={classes} data-testid="card" {...rest}>
      {media && (
        <div className={`ui-card__media ui-card__media--${mediaFit}`} data-testid="card-media">
          {media}
          {status && <span className="ui-card__status">{status}</span>}
        </div>
      )}
      {children && <div className="ui-card__body">{children}</div>}
    </Element>
  );
}

/** 16/500 title, two lines at most. */
export function CardTitle({ children, as: Heading = 'h3' }: { children: ReactNode; as?: 'h2' | 'h3' | 'h4' }) {
  return <Heading className="ui-card__title">{children}</Heading>;
}

/** 13/400 muted meta line, e.g. "Sun 8 Nov · 15:00–16:00 · Online". */
export function CardMeta({ children }: { children: ReactNode }) {
  return <p className="ui-card__meta">{children}</p>;
}
